import React, { useEffect, useState } from "react";
import {
  X,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  Building2,
  Store,
  User,
  Tag,
  FileText,
  Paperclip,
  ExternalLink,
  Download,
  Trash2,
  CheckCircle2,
  ZoomIn,
  Lock,
  Unlock,
  Save,
  Plus,
  Loader2,
  Sparkles,
  Edit3,
  Flag,
  Building,
  ShieldCheck,
} from "lucide-react";
import {
  api,
  formatCurrencyPrecise,
  formatDate,
  getReceiptUrl,
  CATEGORIES_EXPENSE,
  CATEGORIES_INCOME,
} from "../lib/api";
import { toast } from "sonner";
import DeductibilityStatusPill from "./DeductibilityStatusPill";
import MortgageBreakdownCard from "./MortgageBreakdownCard";

// Smart normalizer for fuzzy merchant matching
const normalizeMerchant = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/^(the|a|an)\s+/i, "")
    .replace(/[^a-z0-9]/g, "");

export default function TransactionDetailModal({
  transaction,
  onClose,
  onDelete,
  onUpdate,
  canDelete = false,
  properties: propProperties = null,
  vendors: propVendors = null,
  tenants: propTenants = null,
}) {
  const [currentTx, setCurrentTx] = useState(transaction);
  const [isLocked, setIsLocked] = useState(true);
  const [isZoomed, setIsZoomed] = useState(false);
  const [saving, setSaving] = useState(false);

  // Entities for dropdowns
  const [properties, setProperties] = useState(propProperties || []);
  const [vendors, setVendors] = useState(propVendors || []);
  const [tenants, setTenants] = useState(propTenants || []);

  // Quick inline add vendor state
  const [showQuickAddVendor, setShowQuickAddVendor] = useState(false);
  const [newVendorName, setNewVendorName] = useState("");
  const [creatingVendor, setCreatingVendor] = useState(false);

  // Form state for editing
  const [editForm, setEditForm] = useState({
    date: transaction?.date || "",
    amount: transaction?.amount != null ? transaction.amount.toString() : "",
    type: transaction?.type || "Expense",
    category: transaction?.category || "Repairs",
    property_id: transaction?.property_id || "",
    vendor_id: transaction?.vendor_id || "",
    tenant_id: transaction?.tenant_id || "",
    notes: transaction?.notes || "",
    deductible_pct: transaction?.deductible_pct != null ? transaction.deductible_pct : 100,
    deductible_amount: transaction?.deductible_amount != null ? transaction.deductible_amount.toString() : "",
    principal_amount: transaction?.principal_amount || 0,
    interest_amount: transaction?.interest_amount || 0,
    escrow_amount: transaction?.escrow_amount || 0,
    deductibility_reason: transaction?.deductibility_reason || "",
    needs_cpa_review: Boolean(transaction?.needs_cpa_review),
    cpa_review_reason: transaction?.cpa_review_reason || "",
    is_capital_improvement: Boolean(transaction?.is_capital_improvement),
    cpa_resolved: Boolean(transaction?.cpa_resolved),
    cpa_notes: transaction?.cpa_notes || "",
  });

  // Sync state if transaction prop changes
  useEffect(() => {
    setCurrentTx(transaction);
    if (transaction) {
      setEditForm({
        date: transaction.date || "",
        amount: transaction.amount != null ? transaction.amount.toString() : "",
        type: transaction.type || "Expense",
        category: transaction.category || "Repairs",
        property_id: transaction.property_id || "",
        vendor_id: transaction.vendor_id || "",
        tenant_id: transaction.tenant_id || "",
        notes: transaction.notes || "",
        deductible_pct: transaction.deductible_pct != null ? transaction.deductible_pct : 100,
        deductible_amount: transaction.deductible_amount != null ? transaction.deductible_amount.toString() : "",
        principal_amount: transaction.principal_amount || 0,
        interest_amount: transaction.interest_amount || 0,
        escrow_amount: transaction.escrow_amount || 0,
        deductibility_reason: transaction.deductibility_reason || "",
        needs_cpa_review: Boolean(transaction.needs_cpa_review),
        cpa_review_reason: transaction.cpa_review_reason || "",
        is_capital_improvement: Boolean(transaction.is_capital_improvement),
        cpa_resolved: Boolean(transaction.cpa_resolved),
        cpa_notes: transaction.cpa_notes || "",
      });
    }
  }, [transaction]);

  // Fetch properties/vendors/tenants if not supplied
  useEffect(() => {
    if (!propProperties) {
      api.get("/properties").then((r) => setProperties(r.data || [])).catch(() => {});
    }
    if (!propVendors) {
      api.get("/vendors").then((r) => setVendors(r.data || [])).catch(() => {});
    }
    if (!propTenants) {
      api.get("/tenants").then((r) => setTenants(r.data || [])).catch(() => {});
    }
  }, [propProperties, propVendors, propTenants]);

  // Keyboard escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Tuck away bottom navigation menu while modal is open
  useEffect(() => {
    document.body.classList.add("has-modal-open");
    return () => {
      document.body.classList.remove("has-modal-open");
    };
  }, []);

  if (!currentTx) return null;


  const isIncome = currentTx.type === "Income";
  const receiptUrl = getReceiptUrl(currentTx.receipt_path);
  const isPdf = currentTx.receipt_path?.toLowerCase().endsWith(".pdf");

  // Extract detected merchant name from notes or OCR data
  let detectedVendorName = "";
  if (currentTx.notes && /receipt:\s*/i.test(currentTx.notes)) {
    detectedVendorName = currentTx.notes.replace(/^receipt:\s*/i, "").trim();
  }

  // Find matching vendor by name or normalized name
  const matchedExistingVendor = detectedVendorName
    ? vendors.find((v) => {
        const normV = normalizeMerchant(v.name);
        const normD = normalizeMerchant(detectedVendorName);
        return normV === normD || normV.includes(normD) || normD.includes(normV);
      })
    : null;

  // Parse itemized line items if available
  let parsedItems = [];
  if (currentTx.receipt_items) {
    try {
      if (typeof currentTx.receipt_items === "string") {
        parsedItems = JSON.parse(currentTx.receipt_items);
      } else if (Array.isArray(currentTx.receipt_items)) {
        parsedItems = currentTx.receipt_items;
      }
    } catch (e) {
      console.error("Failed to parse receipt items:", e);
    }
  }

  // Quick add vendor handler
  const handleCreateVendor = async (nameToCreate) => {
    const name = (nameToCreate || newVendorName).trim();
    if (!name) return;
    setCreatingVendor(true);
    try {
      const res = await api.post("/vendors", {
        name,
        category: editForm.category || "Repairs",
      });
      const newV = res.data;
      setVendors((prev) => [...prev, newV]);
      setEditForm((prev) => ({ ...prev, vendor_id: newV.id }));
      setShowQuickAddVendor(false);
      setNewVendorName("");
      toast.success(`Merchant "${name}" added to vendors!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create vendor");
    } finally {
      setCreatingVendor(false);
    }
  };

  // Save changes handler
  const handleSave = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    try {
      const amtVal = parseFloat(editForm.amount) || 0;
      const dedPctVal = editForm.type === "Expense" ? (parseFloat(editForm.deductible_pct) || 0) : 100;
      const dedAmtVal = editForm.deductible_amount !== "" && editForm.deductible_amount != null
        ? parseFloat(editForm.deductible_amount)
        : (editForm.type === "Expense" ? Number((amtVal * (dedPctVal / 100)).toFixed(2)) : 0);

      const payload = {
        date: editForm.date,
        amount: amtVal,
        type: editForm.type,
        category: editForm.category,
        property_id: editForm.property_id || null,
        vendor_id: editForm.type === "Expense" ? (editForm.vendor_id || null) : null,
        tenant_id: editForm.type === "Income" ? (editForm.tenant_id || null) : null,
        notes: editForm.notes,
        deductible_pct: dedPctVal,
        deductible_amount: dedAmtVal,
        principal_amount: parseFloat(editForm.principal_amount) || 0,
        interest_amount: parseFloat(editForm.interest_amount) || 0,
        escrow_amount: parseFloat(editForm.escrow_amount) || 0,
        deductibility_reason: editForm.deductibility_reason || null,
        needs_cpa_review: Boolean(editForm.needs_cpa_review),
        cpa_review_reason: editForm.cpa_review_reason || null,
        is_capital_improvement: Boolean(editForm.is_capital_improvement),
        cpa_resolved: Boolean(editForm.cpa_resolved),
        cpa_notes: editForm.cpa_notes || null,
      };
      const res = await api.patch(`/transactions/${currentTx.id}`, payload);
      setCurrentTx(res.data);
      if (onUpdate) onUpdate(res.data);
      setIsLocked(true);
      toast.success("Transaction updated successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update transaction");
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditForm({
      date: currentTx.date || "",
      amount: currentTx.amount != null ? currentTx.amount.toString() : "",
      type: currentTx.type || "Expense",
      category: currentTx.category || "Repairs",
      property_id: currentTx.property_id || "",
      vendor_id: currentTx.vendor_id || "",
      tenant_id: currentTx.tenant_id || "",
      notes: currentTx.notes || "",
      deductible_pct: currentTx.deductible_pct != null ? currentTx.deductible_pct : 100,
      deductible_amount: currentTx.deductible_amount != null ? currentTx.deductible_amount.toString() : "",
      principal_amount: currentTx.principal_amount || 0,
      interest_amount: currentTx.interest_amount || 0,
      escrow_amount: currentTx.escrow_amount || 0,
      deductibility_reason: currentTx.deductibility_reason || "",
      needs_cpa_review: Boolean(currentTx.needs_cpa_review),
      cpa_review_reason: currentTx.cpa_review_reason || "",
      is_capital_improvement: Boolean(currentTx.is_capital_improvement),
      cpa_resolved: Boolean(currentTx.cpa_resolved),
      cpa_notes: currentTx.cpa_notes || "",
    });
    setIsLocked(true);
    setShowQuickAddVendor(false);
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto overscroll-contain scroll-smooth animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="transaction-detail-modal"
    >
      <div
        className="card w-full max-w-xl sm:max-w-2xl p-0 overflow-hidden shadow-2xl rounded-t-3xl sm:rounded-3xl max-h-[92vh] sm:max-h-[90vh] flex flex-col my-0 sm:my-auto"
        onClick={(e) => e.stopPropagation()}
      >

        {/* Header */}
        <div className="p-4 sm:p-5 border-b border-[var(--border)] flex items-start justify-between bg-[var(--surface-hover)] min-w-0">
          <div className="flex items-center gap-3 min-w-0 flex-1 mr-2">
            <div
              className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 shadow-sm ${
                isIncome
                  ? "bg-emerald-500/15 text-emerald-400"
                  : "bg-red-500/15 text-red-400"
              }`}
            >
              {isIncome ? <ArrowUpRight size={22} /> : <ArrowDownRight size={22} />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-extrabold text-lg text-[var(--text)] truncate">
                  {currentTx.category}
                </span>
                <span
                  className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider shrink-0 ${
                    isIncome
                      ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
                      : "bg-red-500/15 text-red-400 border border-red-500/30"
                  }`}
                >
                  {currentTx.type}
                </span>
              </div>
              <div className="text-xs text-[var(--muted)] mt-0.5 truncate">
                Transaction ID: <span className="font-mono text-[11px]">{currentTx.id?.slice(0, 12)}...</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {/* Lock / Unlock Toggle Button */}
            <button
              type="button"
              onClick={() => {
                if (!isLocked) {
                  handleCancelEdit();
                } else {
                  setIsLocked(false);
                }
              }}
              className={`px-3 py-1.5 rounded-xl border text-xs font-semibold flex items-center gap-1.5 transition-colors ${
                isLocked
                  ? "bg-amber-500/10 text-amber-400 border-amber-500/30 hover:bg-amber-500/20"
                  : "bg-emerald-500/15 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/25"
              }`}
              title={
                isLocked
                  ? "Protected from accidental changes. Click to unlock & edit."
                  : "Currently unlocked for editing. Click to cancel and lock."
              }
              data-testid="tx-lock-toggle"
            >
              {isLocked ? <Lock size={13} /> : <Unlock size={13} />}
              <span>{isLocked ? "Locked" : "Unlocked"}</span>
            </button>

            <button
              onClick={onClose}
              className="p-1.5 rounded-xl text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--surface)] transition-colors"
              title="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Scrollable Body */}
        <div className="p-4 sm:p-6 space-y-6 overflow-y-auto overflow-x-hidden flex-1 min-w-0 overscroll-contain scroll-smooth">
          {/* Edit Mode Warning Banner */}
          {!isLocked && (
            <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-between text-xs text-emerald-400 animate-in fade-in duration-150">
              <div className="flex items-center gap-2">
                <Edit3 size={15} className="shrink-0" />
                <span>
                  <strong>Edit Mode Unlocked:</strong> Modify property, vendor, amount, date, or category below and click Save.
                </span>
              </div>
            </div>
          )}

          {/* VIEW MODE (LOCKED) */}
          {isLocked ? (
            <div className="space-y-6">
              {/* Amount & Date Hero */}
              <div className="p-4 rounded-2xl bg-[var(--surface-hover)] border border-[var(--border)] flex items-center justify-between">
                <div>
                  <div className="text-xs text-[var(--muted)] font-medium">Total Amount</div>
                  <div
                    className={`text-3xl font-black tabular-nums tracking-tight mt-0.5 ${
                      isIncome ? "text-emerald-400" : "text-[var(--text)]"
                    }`}
                  >
                    {isIncome ? "+" : "−"}{formatCurrencyPrecise(currentTx.amount)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-[var(--muted)] font-medium flex items-center justify-end gap-1">
                    <Calendar size={13} />
                    <span>Date</span>
                  </div>
                  <div className="text-base font-bold text-[var(--text)] mt-0.5">
                    {formatDate(currentTx.date)}
                  </div>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="p-3.5 rounded-xl bg-[var(--surface-hover)]/60 border border-[var(--border)] space-y-1">
                  <div className="text-xs text-[var(--muted)] flex items-center gap-1.5 font-medium">
                    <Building2 size={14} className="text-blue-400" />
                    <span>Property</span>
                  </div>
                  <div className="font-semibold text-[var(--text)] truncate">
                    {currentTx.property_name || "Unassigned"}
                  </div>
                </div>

                <div className="p-3.5 rounded-xl bg-[var(--surface-hover)]/60 border border-[var(--border)] space-y-1">
                  <div className="text-xs text-[var(--muted)] flex items-center gap-1.5 font-medium">
                    {isIncome ? (
                      <User size={14} className="text-emerald-400" />
                    ) : (
                      <Store size={14} className="text-amber-400" />
                    )}
                    <span>{isIncome ? "Tenant" : "Vendor / Merchant"}</span>
                  </div>
                  <div className="font-semibold text-[var(--text)] truncate">
                    {currentTx.vendor_name || currentTx.tenant_name || "—"}
                  </div>
                </div>

                <div className="p-3.5 rounded-xl bg-[var(--surface-hover)]/60 border border-[var(--border)] space-y-1">
                  <div className="text-xs text-[var(--muted)] flex items-center gap-1.5 font-medium">
                    <Tag size={14} className="text-purple-400" />
                    <span>Tax Category</span>
                  </div>
                  <div className="font-semibold text-[var(--text)]">
                    {currentTx.category}
                    <span className="text-[11px] text-[var(--muted)] block font-normal">
                      Schedule E Deductible
                    </span>
                  </div>
                </div>

                <div className="p-3.5 rounded-xl bg-[var(--surface-hover)]/60 border border-[var(--border)] space-y-1">
                  <div className="text-xs text-[var(--muted)] flex items-center gap-1.5 font-medium">
                    <FileText size={14} className="text-teal-400" />
                    <span>Notes & Memo</span>
                  </div>
                  <div className="font-medium text-[var(--text)] break-words">
                    {currentTx.notes || "No notes entered"}
                  </div>
                </div>

                {/* Mortgage Breakdown in View Mode */}
                {!isIncome && (currentTx.category === "Mortgage Interest" || currentTx.principal_amount > 0) && (
                  <div className="sm:col-span-2">
                    <MortgageBreakdownCard
                      amount={currentTx.amount}
                      principalAmount={currentTx.principal_amount}
                      interestAmount={currentTx.interest_amount}
                      escrowAmount={currentTx.escrow_amount}
                      deductibleAmount={currentTx.deductible_amount}
                      deductiblePct={currentTx.deductible_pct}
                      readOnly={true}
                    />
                  </div>
                )}

                {/* Tax Deductibility & CPA Review Status */}
                {!isIncome && (
                  <div className="sm:col-span-2 p-3.5 rounded-xl bg-[var(--surface-hover)]/80 border border-[var(--border)] space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-[var(--muted)] flex items-center gap-1.5 font-medium">
                        <ShieldCheck size={14} className="text-emerald-400" />
                        <span>Tax Deductibility Status</span>
                      </div>
                      <DeductibilityStatusPill
                        deductiblePct={currentTx.deductible_pct}
                        isCapitalImprovement={currentTx.is_capital_improvement}
                        needsCpaReview={currentTx.needs_cpa_review}
                        cpaResolved={currentTx.cpa_resolved}
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs pt-1">
                      <div>
                        <span className="text-[var(--muted)]">Deductible Portion: </span>
                        <strong className="text-[var(--text)]">
                          ${(currentTx.deductible_amount != null && currentTx.deductible_amount !== "" ? Number(currentTx.deductible_amount) : ((Number(currentTx.amount) || 0) * ((currentTx.deductible_pct ?? 100) / 100))).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({currentTx.deductible_pct ?? 100}%)
                        </strong>
                      </div>
                      <div>
                        <span className="text-[var(--muted)]">Classification: </span>
                        <span className="text-[var(--text)]">
                          {currentTx.is_capital_improvement
                            ? "Capital Improvement (Depreciated over 27.5 yrs)"
                            : (currentTx.deductibility_reason || "Standard business expense")}
                        </span>
                      </div>
                    </div>

                    {currentTx.needs_cpa_review && (
                      <div className="mt-2 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs text-amber-300 space-y-1">
                        <div className="font-semibold flex items-center gap-1.5 text-amber-400">
                          <Flag size={13} className="fill-amber-400" />
                          <span>Flagged for CPA Review — {currentTx.cpa_resolved ? "Resolved" : "Pending"}</span>
                        </div>
                        <p className="text-[11px] text-[var(--text)]">
                          Reason: {currentTx.cpa_review_reason || currentTx.deductibility_reason || "CPA review required"}
                        </p>
                        {currentTx.cpa_notes && (
                          <p className="text-[11px] text-[var(--muted)] italic">
                            CPA Notes: {currentTx.cpa_notes}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* EDIT MODE (UNLOCKED) */
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">Date</label>
                  <input
                    type="date"
                    className="lapen-input"
                    value={editForm.date}
                    onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <label className="label">Total Amount ($)</label>
                  <input
                    type="number"
                    step="any"
                    className="lapen-input font-bold"
                    value={editForm.amount}
                    onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                    placeholder="0.00"
                    required
                  />
                </div>

                <div>
                  <label className="label">Property</label>
                  <select
                    className="lapen-input"
                    value={editForm.property_id || ""}
                    onChange={(e) => setEditForm({ ...editForm, property_id: e.target.value })}
                  >
                    <option value="">— Unassigned (Portfolio-wide) —</option>
                    {properties.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="label">Tax Category</label>
                  <select
                    className="lapen-input"
                    value={editForm.category}
                    onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                  >
                    {(isIncome ? CATEGORIES_INCOME : CATEGORIES_EXPENSE).map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                {isIncome ? (
                  <div className="sm:col-span-2">
                    <label className="label">Tenant</label>
                    <select
                      className="lapen-input"
                      value={editForm.tenant_id || ""}
                      onChange={(e) => setEditForm({ ...editForm, tenant_id: e.target.value })}
                    >
                      <option value="">— None —</option>
                      {tenants.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="sm:col-span-2 space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="label mb-0">Vendor / Merchant</label>
                      <button
                        type="button"
                        onClick={() => setShowQuickAddVendor(!showQuickAddVendor)}
                        className="text-xs text-teal-400 hover:text-teal-300 flex items-center gap-1 font-medium"
                      >
                        <Plus size={13} /> {showQuickAddVendor ? "Cancel Add" : "+ New Vendor"}
                      </button>
                    </div>

                    <select
                      className="lapen-input"
                      value={editForm.vendor_id || ""}
                      onChange={(e) => setEditForm({ ...editForm, vendor_id: e.target.value })}
                    >
                      <option value="">— None —</option>
                      {vendors.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name} ({v.category || "Vendor"})
                        </option>
                      ))}
                    </select>

                    {/* Smart Merchant Suggestion Banner from Receipt */}
                    {detectedVendorName && (
                      <div className="p-3 rounded-xl bg-teal-500/10 border border-teal-500/30 text-xs flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div>
                          <div className="flex items-center gap-1.5 font-semibold text-teal-400">
                            <Sparkles size={13} />
                            <span>Detected on Receipt: "{detectedVendorName}"</span>
                          </div>
                          {matchedExistingVendor ? (
                            <p className="text-[11px] text-[var(--muted)] mt-0.5">
                              Matches existing vendor: <strong>{matchedExistingVendor.name}</strong>
                            </p>
                          ) : (
                            <p className="text-[11px] text-[var(--muted)] mt-0.5">
                              This merchant is not in your vendor list yet.
                            </p>
                          )}
                        </div>

                        <div className="shrink-0 flex items-center gap-2">
                          {matchedExistingVendor ? (
                            editForm.vendor_id !== matchedExistingVendor.id && (
                              <button
                                type="button"
                                onClick={() =>
                                  setEditForm({ ...editForm, vendor_id: matchedExistingVendor.id })
                                }
                                className="px-2.5 py-1 rounded-lg bg-teal-500/20 text-teal-300 font-semibold hover:bg-teal-500/30 transition-colors"
                              >
                                Select {matchedExistingVendor.name}
                              </button>
                            )
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleCreateVendor(detectedVendorName)}
                              disabled={creatingVendor}
                              className="px-2.5 py-1 rounded-lg bg-teal-500 text-white font-semibold hover:bg-teal-600 transition-colors flex items-center gap-1 shadow-sm"
                            >
                              {creatingVendor ? (
                                <Loader2 size={12} className="animate-spin" />
                              ) : (
                                <Plus size={12} />
                              )}
                              <span>Add "{detectedVendorName}" as Vendor</span>
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Quick Inline New Vendor Input */}
                    {showQuickAddVendor && (
                      <div className="p-3 rounded-xl border border-[var(--border)] bg-[var(--surface-hover)] space-y-2 mt-2">
                        <div className="text-xs font-semibold text-[var(--text)]">Create New Vendor</div>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            className="lapen-input text-xs py-1.5 flex-1"
                            placeholder="Vendor or Merchant name"
                            value={newVendorName}
                            onChange={(e) => setNewVendorName(e.target.value)}
                          />
                          <button
                            type="button"
                            onClick={() => handleCreateVendor(newVendorName)}
                            disabled={creatingVendor || !newVendorName.trim()}
                            className="btn-primary text-xs py-1.5 px-3 shrink-0 flex items-center gap-1"
                          >
                            {creatingVendor && <Loader2 size={12} className="animate-spin" />}
                            <span>Create & Select</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="sm:col-span-2">
                  <label className="label">Notes & Description</label>
                  <input
                    type="text"
                    className="lapen-input"
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    placeholder="e.g. Repairs completed by technician"
                  />
                </div>

                {!isIncome && (editForm.category === "Mortgage Interest" || editForm.principal_amount > 0) && (
                  <div className="sm:col-span-2">
                    <MortgageBreakdownCard
                      amount={editForm.amount}
                      principalAmount={editForm.principal_amount}
                      interestAmount={editForm.interest_amount}
                      escrowAmount={editForm.escrow_amount}
                      deductibleAmount={editForm.deductible_amount}
                      deductiblePct={editForm.deductible_pct}
                      onChange={(fields) => setEditForm((prev) => ({ ...prev, ...fields }))}
                    />
                  </div>
                )}

                {!isIncome && (
                  <div className="sm:col-span-2 p-3.5 rounded-xl border border-[var(--border)] bg-[var(--surface-hover)]/40 space-y-3">
                    <div className="text-xs font-bold uppercase tracking-wider text-[var(--muted)] flex items-center gap-1.5">
                      <ShieldCheck size={14} className="text-emerald-400" />
                      <span>Tax Deductibility & CPA Settings</span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="label">Deductible Percentage (%)</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="any"
                          className="lapen-input"
                          value={editForm.deductible_pct ?? 100}
                          onChange={(e) => setEditForm({ ...editForm, deductible_pct: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="label">Deductibility Reason</label>
                        <input
                          type="text"
                          className="lapen-input"
                          value={editForm.deductibility_reason || ""}
                          onChange={(e) => setEditForm({ ...editForm, deductibility_reason: e.target.value })}
                          placeholder="e.g. 50% business meal, 100% routine repair"
                        />
                      </div>
                    </div>

                    <div className="space-y-2 pt-1 border-t border-[var(--border)]">
                      <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-[var(--text)]">
                        <input
                          type="checkbox"
                          checked={editForm.is_capital_improvement}
                          onChange={(e) => setEditForm({
                            ...editForm,
                            is_capital_improvement: e.target.checked,
                            deductible_pct: e.target.checked ? 0 : editForm.deductible_pct,
                          })}
                          className="rounded border-[var(--border)] text-indigo-600 focus:ring-indigo-500"
                        />
                        <Building size={13} className="text-indigo-400" />
                        <span>Capital Improvement (Must be depreciated over 27.5 yrs on Form 4562)</span>
                      </label>

                      <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-[var(--text)]">
                        <input
                          type="checkbox"
                          checked={editForm.needs_cpa_review}
                          onChange={(e) => setEditForm({ ...editForm, needs_cpa_review: e.target.checked })}
                          className="rounded border-[var(--border)] text-amber-500 focus:ring-amber-500"
                        />
                        <Flag size={13} className="text-amber-400" />
                        <span>Flag this transaction for CPA Review</span>
                      </label>

                      {editForm.needs_cpa_review && (
                        <div className="space-y-2 pl-5 pt-1">
                          <input
                            type="text"
                            className="lapen-input text-xs"
                            value={editForm.cpa_review_reason || ""}
                            onChange={(e) => setEditForm({ ...editForm, cpa_review_reason: e.target.value })}
                            placeholder="Reason for CPA review (e.g. repair vs capitalization determination)"
                          />
                          <div className="flex items-center gap-3">
                            <label className="flex items-center gap-1.5 text-xs text-[var(--muted)]">
                              <input
                                type="checkbox"
                                checked={editForm.cpa_resolved}
                                onChange={(e) => setEditForm({ ...editForm, cpa_resolved: e.target.checked })}
                                className="rounded"
                              />
                              <span>Mark as CPA Resolved</span>
                            </label>
                          </div>
                          <input
                            type="text"
                            className="lapen-input text-xs"
                            value={editForm.cpa_notes || ""}
                            onChange={(e) => setEditForm({ ...editForm, cpa_notes: e.target.value })}
                            placeholder="CPA Resolution Notes (optional)"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </form>
          )}

          {/* Itemized Lines (if receipt items exist) */}
          {parsedItems.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-bold uppercase tracking-wider text-[var(--muted)]">
                  Itemized Line Items ({parsedItems.length})
                </h4>
              </div>
              <div className="rounded-xl border border-[var(--border)] overflow-hidden divide-y divide-[var(--border)]">
                {parsedItems.map((it, idx) => (
                  <div
                    key={idx}
                    className="p-2.5 px-3.5 flex items-center justify-between text-xs bg-[var(--surface-hover)]/40 gap-3"
                  >
                    <div className="flex-1 min-w-0 truncate" title={it.description}>
                      <span className="font-medium text-[var(--text)]">{it.description}</span>
                      {it.quantity > 1 && (
                        <span className="text-[11px] text-[var(--muted)] ml-1.5">
                          (Qty: {it.quantity})
                        </span>
                      )}
                    </div>
                    <div className="font-bold tabular-nums text-[var(--text)] shrink-0">
                      {formatCurrencyPrecise(it.amount)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Audit Receipt & Document Attachment */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Paperclip size={15} className="text-teal-400" />
                <h4 className="text-xs font-bold uppercase tracking-wider text-[var(--muted)]">
                  Attached Audit Receipt
                </h4>
              </div>
              {currentTx.receipt_path && (
                <span className="text-[11px] text-teal-400 bg-teal-500/10 border border-teal-500/30 px-2 py-0.5 rounded-full font-semibold flex items-center gap-1">
                  <CheckCircle2 size={12} /> Stored on NAS
                </span>
              )}
            </div>

            {currentTx.receipt_path ? (
              <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-hover)]/40 p-3 sm:p-4 space-y-3">
                {isPdf ? (
                  <div className="p-3 sm:p-4 rounded-xl border border-[var(--border)] bg-[var(--surface)] flex flex-col sm:flex-row sm:items-center justify-between gap-3 min-w-0">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-xl bg-red-500/15 text-red-400 flex items-center justify-center shrink-0">
                        <FileText size={20} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div
                          className="text-sm font-semibold text-[var(--text)] truncate"
                          title={currentTx.receipt_path.split("/").pop()}
                        >
                          {currentTx.receipt_path.split("/").pop()}
                        </div>
                        <div className="text-xs text-[var(--muted)]">PDF Audit Document</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 self-end sm:self-auto">
                      <a
                        href={receiptUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5 shrink-0"
                      >
                        <ExternalLink size={13} /> Open
                      </a>
                      <a
                        href={receiptUrl}
                        download
                        className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5 shrink-0"
                      >
                        <Download size={13} /> Download
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div
                      className="relative group rounded-xl overflow-hidden border border-[var(--border)] bg-black/40 cursor-pointer flex items-center justify-center max-h-80"
                      onClick={() => setIsZoomed(!isZoomed)}
                      title="Click to toggle full size"
                    >
                      <img
                        src={receiptUrl}
                        alt="Transaction Receipt"
                        className={`w-full object-contain transition-transform duration-200 ${
                          isZoomed ? "max-h-none py-2 scale-105" : "max-h-64"
                        }`}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "";
                          e.target.alt = "Receipt image could not be loaded";
                        }}
                      />
                      <div className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 text-xs">
                        <ZoomIn size={14} />
                        <span>{isZoomed ? "Zoom out" : "Zoom in"}</span>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pt-1 min-w-0">
                      <div
                        className="text-xs text-[var(--muted)] truncate min-w-0 flex-1"
                        title={currentTx.receipt_path.split("/").pop()}
                      >
                        File: <span className="font-mono text-[11px]">{currentTx.receipt_path.split("/").pop()}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 self-end sm:self-auto">
                        <a
                          href={receiptUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-teal-400 hover:text-teal-300 font-medium flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-teal-500/10 transition-colors shrink-0"
                        >
                          <ExternalLink size={13} /> Open Full Size
                        </a>
                        <a
                          href={receiptUrl}
                          download
                          className="text-xs text-[var(--muted)] hover:text-[var(--text)] font-medium flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-[var(--surface)] transition-colors shrink-0"
                        >
                          <Download size={13} /> Download
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-4 rounded-xl border border-dashed border-[var(--border)] text-center text-xs text-[var(--muted)]">
                No receipt or invoice attachment stored for this transaction.
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-4 pb-[calc(env(safe-area-inset-bottom,12px)+12px)] sm:pb-4 border-t border-[var(--border)] bg-[var(--surface-hover)] flex items-center justify-between gap-2">

          <div>
            {isLocked ? (
              canDelete && onDelete && (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm("Are you sure you want to delete this transaction? This cannot be undone.")) {
                      onDelete(currentTx.id);
                      onClose();
                    }
                  }}
                  className="text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 px-3 py-2 rounded-xl transition-colors flex items-center gap-1.5 font-medium"
                >
                  <Trash2 size={14} /> Delete Transaction
                </button>
              )
            ) : (
              <button
                type="button"
                onClick={handleCancelEdit}
                className="btn-secondary text-xs py-2 px-3"
              >
                Cancel Edits
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {isLocked ? (
              <>
                <button
                  type="button"
                  onClick={() => setIsLocked(false)}
                  className="btn-primary text-xs py-2 px-3.5 flex items-center gap-1.5"
                >
                  <Edit3 size={13} /> Edit Expense
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="btn-secondary text-xs py-2 px-4"
                >
                  Close
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="btn-primary text-xs py-2 px-4 flex items-center gap-1.5"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                <span>Save Changes</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
